import { useState, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { useToast } from '@/storybook/components/Toast/Toast';
import { UserFilterListItem, UserFilterUpdateRequest } from '@/models/user/userFilter';
import { UserFilterApiService } from '@/api/userProfile/UserFilterApiService';
import { AppliedFilterSetStorage } from '@/utils/filter/appliedFilterSetStorage';
import useApplyUserFilter from '@/hooks/useApplyUserFilter';
import fetchFilterPayload from '@/utils/filter/fetchFilterPayload';
import { isLocalFiltersRecord } from '@/utils/filter/isLocalFiltersRecord';
import { readCachedCountryCodes } from '@/models/filter/configs/general/countryFilter';
import { useAuthContext } from '@/context/AuthContext';
import { copyShareViewLink, publishSharedView, type SharedViewPayload } from '@/utils/filter/shareView';

type ActiveDialog = { type: 'details' | 'rename' | 'delete'; filter: UserFilterListItem };

const readAppliedSetPayload = (userFilterId: string, senderCountryCodes: string[]): SharedViewPayload | null => {
  const applied = AppliedFilterSetStorage.get();
  if (applied?.userFilterId !== userFilterId) return null;

  return {
    globalFilters: applied.globalFilters,
    localFilters: isLocalFiltersRecord(applied.localFilters) ? applied.localFilters : undefined,
    resolvedCountryCodes: readCachedCountryCodes(),
    senderCountryCodes,
  };
};

const readStoredPayload = async (userFilterId: string): Promise<SharedViewPayload> => {
  const payload = await fetchFilterPayload(userFilterId);

  return {
    globalFilters: payload.globalFilters,
    localFilters: payload.localFilters,
    resolvedCountryCodes: payload.resolvedCountryCodes,
    senderCountryCodes: payload.senderCountryCodes ?? [],
  };
};

const useManageFiltersActions = (
  onFiltersChanged: () => void,
  onClose: () => void,
  filters: UserFilterListItem[] = [],
) => {
  const { t } = useTranslation();
  const { makeToast } = useToast();
  const { userInfo } = useAuthContext();

  const { applyFilter, isApplying, validationDialogProps } = useApplyUserFilter({
    filters,
    onBeforeApply: onClose,
  });

  const [activeDialog, setActiveDialog] = useState<ActiveDialog | null>(null);
  const [isDialogLoading, setIsDialogLoading] = useState(false);
  const [isSettingDefault, setIsSettingDefault] = useState(false);
  const sharingRef = useRef(false);
  const sharedIdCacheRef = useRef(new Map<string, string>());

  const closeDialog = useCallback(() => {
    setActiveDialog(null);
    setIsDialogLoading(false);
  }, []);

  const handleSeeDetails = useCallback((filter: UserFilterListItem) => {
    setActiveDialog({ type: 'details', filter });
  }, []);

  const handleRename = useCallback((filter: UserFilterListItem) => {
    setActiveDialog({ type: 'rename', filter });
  }, []);

  const handleDelete = useCallback((filter: UserFilterListItem) => {
    setActiveDialog({ type: 'delete', filter });
  }, []);

  const handleRenameSave = useCallback(
    async (filterName: string, isDefault: boolean) => {
      if (!activeDialog || activeDialog.type !== 'rename') return;
      setIsDialogLoading(true);
      const isNameChanged = filterName !== (activeDialog.filter.filterName ?? '');
      const payload: UserFilterUpdateRequest = isNameChanged ? { filterName, isDefault } : { isDefault };
      try {
        await UserFilterApiService.updateFilter(activeDialog.filter.userFilterId, payload);
        AppliedFilterSetStorage.update(activeDialog.filter.userFilterId, payload);
        closeDialog();
        onFiltersChanged();
        makeToast({ content: String(t('global.filters.manageSavedFilters.renameSuccess')), appearance: 'success' });
      } catch (error) {
        setIsDialogLoading(false);
        const apiMessage =
          axios.isAxiosError(error) && typeof error.response?.data?.message === 'string'
            ? error.response.data.message
            : undefined;
        makeToast({
          content: apiMessage ?? String(t('global.filters.manageSavedFilters.renameError')),
          appearance: 'error',
        });
      }
    },
    [activeDialog, closeDialog, onFiltersChanged, makeToast, t],
  );

  const handleDeleteConfirm = useCallback(async () => {
    if (!activeDialog || activeDialog.type !== 'delete') return;
    setIsDialogLoading(true);
    try {
      await UserFilterApiService.deleteFilter(activeDialog.filter.userFilterId);
      closeDialog();
      onFiltersChanged();
      makeToast({ content: String(t('global.filters.manageSavedFilters.deleteSuccess')), appearance: 'success' });
    } catch {
      setIsDialogLoading(false);
      makeToast({ content: String(t('global.filters.manageSavedFilters.deleteError')), appearance: 'error' });
    }
  }, [activeDialog, closeDialog, onFiltersChanged, makeToast, t]);

  const handleToggleDefault = useCallback(
    async (filter: UserFilterListItem, isDefault: boolean) => {
      setIsSettingDefault(true);
      try {
        await UserFilterApiService.updateFilter(filter.userFilterId, { isDefault });
        onFiltersChanged();
      } catch {
        makeToast({ content: String(t('global.filters.manageSavedFilters.defaultError')), appearance: 'error' });
      } finally {
        setIsSettingDefault(false);
      }
    },
    [onFiltersChanged, makeToast, t],
  );

  const handleSetAsDefault = useCallback(
    (filter: UserFilterListItem) => handleToggleDefault(filter, true),
    [handleToggleDefault],
  );

  const handleDeselectDefault = useCallback(
    (filter: UserFilterListItem) => handleToggleDefault(filter, false),
    [handleToggleDefault],
  );

  const handleShareFilter = useCallback(
    async (filter: UserFilterListItem) => {
      if (sharingRef.current) return;
      sharingRef.current = true;
      try {
        let sharedFilterId = sharedIdCacheRef.current.get(filter.userFilterId);

        if (!sharedFilterId) {
          const senderCountryCodes = userInfo?.countries ?? [];
          const payload =
            readAppliedSetPayload(filter.userFilterId, senderCountryCodes) ??
            (await readStoredPayload(filter.userFilterId));

          sharedFilterId = await publishSharedView(payload);
          sharedIdCacheRef.current.set(filter.userFilterId, sharedFilterId);
        }

        await copyShareViewLink(sharedFilterId);

        makeToast({ content: String(t('global.filters.manageSavedFilters.shareCopied')), appearance: 'success' });
      } catch {
        makeToast({ content: String(t('global.filters.shareViewError')), appearance: 'error' });
      } finally {
        sharingRef.current = false;
      }
    },
    [makeToast, t, userInfo],
  );

  const handleApplyFilters = useCallback((selectedFilterId: string) => applyFilter(selectedFilterId), [applyFilter]);

  return {
    activeDialog,
    isDialogLoading,
    isApplying,
    isSettingDefault,
    validationDialogProps,
    closeDialog,
    handleSeeDetails,
    handleRename,
    handleDelete,
    handleRenameSave,
    handleDeleteConfirm,
    handleSetAsDefault,
    handleDeselectDefault,
    handleShareFilter,
    handleApplyFilters,
  };
};

export default useManageFiltersActions;
